import React, { useEffect, useState } from "react";
import { CityLoadingDialog } from "./city-loading-dialog/CityLoadingDialog";
import { CityCard } from "./CityCard";
import { CityUIProvider } from "./CityUIContext";
import { CityEditModal } from "./city-edit-modal/CityEditModal";
import { CityDeleteModal } from "./city-delete-modal/CityDeleteModal";
import ErrorModal from "../Modal/ErrorModal";
import { toAbsoluteUrl } from "../../../_metronic/_helpers";
import SVG from "react-inlinesvg";
import {
  addCityApi,
  cityListApi,
  deleteCityApi,
  editCityApi,
  stateListAllApi,
} from "../../../axios/services/services";
import UpdatedSuccessfulModal from "../Modal/UpdatedSuccessfulModal";
import AddNewCityModal from "../Modal/AddNewCityModal";
import { useFormik } from "formik";
import { AddCitySchema, EditCitySchema } from "../../FormikSchema/FormikSchema";
import { logger } from "../../utils/Debug";
import { initialFilter } from "./CityUIHelpers";
import { isUnauthorized, trimmer } from "../../utils/utils";

const AddCityInitialValues = {
  name: "",
  status: "1",
  state_name: "",
};

const EditCityInitialValues = {
  name: "",
  status: "1",
  state_name: "",
};

export function CityPage({ history }) {
  const [isLoading, setLoading] = useState(false);
  const [isLoadingAddNewCity, setLoadingAddNewCity] = useState(false);
  const [isLoadingEditCity, setLoadingEditCity] = useState(false);
  const [isLoadingDeleteCity, setLoadingDeleteCity] = useState(false);
  const [data, setData] = useState({});
  const [isDeleteModal, setDeleteModal] = useState(false);
  const [isEditModal, setEditModal] = useState(false);
  const [cityList, setCityList] = useState(null);
  const [stateList, setStateList] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [cityListTotalCount, setCityListTotalCount] = useState(0);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [filterValues, setFilterValues] = useState({});
  const [isAddNewCityModal, setAddNewCityModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [isUpdateCityDetails, setUpdateCityDetails] = useState(0);
  const [queryParams, setQueryParamsBase] = useState(initialFilter);
  const [ids, setIds] = useState([]);

  useEffect(() => {
    async function getCityList() {
      enableLoading();
      const cityListResponse = await cityListApi({
        filterValues,
        queryParams,
        isActiveOnly: 0,
      });
      disableLoading();
      if (cityListResponse.Error) {
        isUnauthorized({ response: cityListResponse });
        setErrorMessage(cityListResponse.Error.message);
        handleErrorModalShow();
        return;
      }
      setCityListTotalCount(
        cityListResponse && cityListResponse.count ? cityListResponse.count : 0
      );
      setCityList(
        cityListResponse && cityListResponse.data ? cityListResponse.data : []
      );
    }
    getCityList();
  }, [filterValues, isUpdateCityDetails, queryParams]);

  useEffect(() => {
    async function getStateList() {
      const stateListResponse = await stateListAllApi({ isActiveOnly: 1 });
      if (stateListResponse.Error) {
        isUnauthorized({ response: stateListResponse });
        setErrorMessage(stateListResponse.Error.message);
        handleErrorModalShow();
        return;
      }

      setStateList(
        stateListResponse && stateListResponse.data
          ? stateListResponse.data
          : []
      );
    }
    getStateList();
  }, []);

  const enableLoading = () => {
    setLoading(true);
  };

  const disableLoading = () => {
    setLoading(false);
  };

  const enableLoadingAddCity = () => {
    setLoadingAddNewCity(true);
  };

  const disableLoadingAddCity = () => {
    setLoadingAddNewCity(false);
  };

  const enableLoadingEditCity = () => {
    setLoadingEditCity(true);
  };

  const disableLoadingEditCity = () => {
    setLoadingEditCity(false);
  };

  const enableLoadingDeleteCity = () => {
    setLoadingDeleteCity(true);
  };

  const disableLoadingDeleteCity = () => {
    setLoadingDeleteCity(false);
  };

  const handleErrorModalShow = () => {
    setIsErrorModalOpen(true);
  };

  const handleErrorModalHide = () => {
    setIsErrorModalOpen(false);
  };

  const handleAddNewCityShow = () => {
    setAddNewCityModal(true);
  };

  const handleAddNewCityHide = () => {
    setAddNewCityModal(false);
  };

  const handleDeleteShow = () => {
    setDeleteModal(true);
  };

  const handleDeleteHide = () => {
    setDeleteModal(false);
  };

  const handleEditShow = () => {
    setEditModal(true);
  };

  const handleEditHide = () => {
    setEditModal(false);
  };

  const handleSuccessModalShow = () => {
    setIsSuccessModalOpen(true);
  };

  const handleSuccessModalHide = () => {
    setIsSuccessModalOpen(false);
  };

  const handleFilterValues = (values) => {
    setFilterValues(values);
  };

  const onClickOfDeleteCityButton = () => {
    deleteCity();
  };

  const handleSetCurrentPage = (page) => {
    setCurrentPage(page);
  };

  const cityUIEvents = {
    openReceiptDialog: (row) => {
      setData(row);
    },
    openDeleteDialog: (row) => {
      setData(row);
      handleDeleteShow();
    },
    openEditDialog: (row) => {
      setData(row);
      // logger("row", row);
      const { stateId, status, name } = row;
      const { state_name } = stateId || {};
      formikEditCity.setFieldValue("name", name);
      formikEditCity.setFieldValue("state_name", state_name);
      formikEditCity.setFieldValue("status", status ? "1" : "0");
      handleEditShow();
    },
  };

  const formikAddCity = useFormik({
    initialValues: AddCityInitialValues,
    validationSchema: AddCitySchema,
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      addNewCity(values, setSubmitting);
    },
    onReset: (values, { resetForm }) => {},
  });

  const formikEditCity = useFormik({
    initialValues: EditCityInitialValues,
    validationSchema: EditCitySchema,
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      editCity(values, setSubmitting);
    },
    onReset: (values, { resetForm }) => {},
  });

  const addNewCity = async (values, setSubmitting) => {
    const { name, state_name, status } = values;
    const [state] = stateList.filter(
      (state) => state.state_name === state_name
    );
    const { id } = state;
    let addCityObj = {
      name: name,
      state_id: id,
      status: parseInt(status) === 1 ? true : false,
    };
    enableLoadingAddCity();
    setSubmitting(true);
    const addCityResponse = await addCityApi(trimmer(addCityObj));
    disableLoadingAddCity();
    setSubmitting(false);
    if (addCityResponse.Error) {
      isUnauthorized({ response: addCityResponse });
      setErrorMessage(addCityResponse.Error.message);
      handleErrorModalShow();
      return;
    }
    setSuccessMessage("City Added Successfully");
    formikAddCity.handleReset();
    setUpdateCityDetails(Math.random());
    handleSuccessModalShow();
    handleAddNewCityHide();
  };

  const editCity = async (values, setSubmitting) => {
    const { id } = data;
    const { state_name, name, status } = values;
    const [state] = stateList.filter(
      (state) => state.state_name === state_name
    );
    const { id: stateId } = state;
    let editCityObj = {
      id,
      name: name,
      state_id: stateId,
      status: parseInt(status) === 1 ? true : false,
    };
    enableLoadingEditCity();
    setSubmitting(true);
    const editCityResponse = await editCityApi(trimmer(editCityObj));
    disableLoadingEditCity();
    setSubmitting(false);
    if (editCityResponse.Error) {
      isUnauthorized({ response: editCityResponse });
      setErrorMessage(editCityResponse.Error.message);
      handleErrorModalShow();
      return;
    }
    setSuccessMessage("City Edited Successfully ");
    setUpdateCityDetails(Math.random());
    handleSuccessModalShow();
    handleEditHide();
  };

  const deleteCity = async () => {
    const { id, name } = data;
    enableLoadingDeleteCity();
    const deleteCityResponse = await deleteCityApi({ id });
    disableLoadingDeleteCity();
    if (deleteCityResponse.Error) {
      isUnauthorized({ response: deleteCityResponse });
      setErrorMessage(deleteCityResponse.Error.message);
      handleErrorModalShow();
      return;
    }
    setSuccessMessage(`${name} City Deleted Successfully`);
    setUpdateCityDetails(Math.random());
    handleSuccessModalShow();
    handleDeleteHide();
  };

  const onClickOfAddNewCityCancelButton = () => {
    formikAddCity.handleReset();
    handleAddNewCityHide();
  };

  const onClickOfEditCityCancelButton = () => {
    formikEditCity.handleReset();
    handleEditHide();
  };

  const handleAddNewCity = () => {
    if (stateList && stateList.length > 0) {
      const [state] = stateList;
      formikAddCity.setFieldValue("state_name", state.state_name);
    }
    handleAddNewCityShow();
  };

  return (
    <>
      <CityUIProvider
        cityUIEvents={cityUIEvents}
        queryParams={queryParams}
        setQueryParamsBase={setQueryParamsBase}
        ids={ids}
        setIds={setIds}
      >
        <CityLoadingDialog />

        {isAddNewCityModal && (
          <AddNewCityModal
            isLoadingAddNewCity={isLoadingAddNewCity}
            stateList={stateList}
            isAddNewCityModal={isAddNewCityModal}
            handleAddNewCityHide={handleAddNewCityHide}
            formik={formikAddCity}
            onClickOfAddNewCityCancelButton={onClickOfAddNewCityCancelButton}
          />
        )}

        {isEditModal && (
          <CityEditModal
            isLoadingEditCity={isLoadingEditCity}
            handleEditHide={handleEditHide}
            isEditModal={isEditModal}
            data={data}
            stateList={stateList}
            formikEditCity={formikEditCity}
            onClickOfEditCityCancelButton={onClickOfEditCityCancelButton}
          />
        )}

        {isDeleteModal && (
          <CityDeleteModal
            isLoadingDeleteCity={isLoadingDeleteCity}
            isDeleteModal={isDeleteModal}
            onClickOfSaveButton={onClickOfDeleteCityButton}
            handleDeleteHide={handleDeleteHide}
          />
        )}

        <CityCard
          isLoading={isLoading}
          handleAddNewCityShow={handleAddNewCity}
          handleFilterValues={handleFilterValues}
          filterValues={filterValues}
          cityList={cityList}
          cityListTotalCount={cityListTotalCount}
          currentPage={currentPage}
          setCurrentPage={handleSetCurrentPage}
        />
      </CityUIProvider>

      <UpdatedSuccessfulModal
        message={successMessage}
        UpdatedSuccessfulModalShow={isSuccessModalOpen}
        UpdatedSuccessfulModalHide={handleSuccessModalHide}
        successIcon={
          <div className="success-icon-wrapper text-center">
            <span className="svg-icon menu-icon svg-icon-white svg-icon-4x mr-0">
              <SVG
                src={toAbsoluteUrl("/media/svg/icons/Navigation/Check.svg")}
              />
            </span>
          </div>
        }
      />
      <ErrorModal
        message={errorMessage}
        isErrorModalOpen={isErrorModalOpen}
        hideErrorModal={handleErrorModalHide}
        errorIcon={
          <div className="danger-icon-wrapper text-center">
            <span className="svg-icon menu-icon svg-icon-white svg-icon-4x mr-0">
              <SVG
                src={toAbsoluteUrl("/media/svg/icons/Navigation/Close.svg")}
              />
            </span>
          </div>
        }
        errorButtonLabel={"Close"}
        errorOnClick={handleErrorModalHide}
      />
    </>
  );
}
